import {
  RetailInvoiceLabels,
  RetailInvoicePDFProps,
} from 'src/components/RetailInvoice';
import { getMarketplacePaymentRelativeUrl } from 'src/utils/marketplacePaymentUtils';
import {
  CatalogResultsSlim,
  ExternalPaymentMethodType,
  MarketplacePaymentLineType,
  MarketplacePaymentLineWithSaleInfo,
  MarketplacePaymentState,
  SaleCostType,
  SalesGroupDetails,
} from 'src/WebApiController';

export interface BuildGroupInvoicePropsArgs {
  /** The full SalesGroup payload fetched via useGetSalesGroup. */
  group: SalesGroupDetails;
  paymentDetails?: MarketplacePaymentLineWithSaleInfo[];
  /** Broker display name — loginContext.user.activeAccount.accountName (same source as the single-sale retail invoice). */
  brokerName: string;
  /** Broker email — loginContext.user.userName (same source as the single-sale retail invoice). */
  brokerEmail?: string;
  /**
   * POS-8630: Batch-resolved catalog (events + venues) fetched via
   * useGetGroupInvoiceCatalog (CatalogClient.getCatalogSlim). Used to fill in
   * event name / date / venue name per order-detail row by matching each member
   * sale's viagVirtId against the events/venues arrays. Optional — when absent
   * (or a sale's id isn't present in it) the row falls back to undefined.
   */
  catalog?: CatalogResultsSlim;
  /**
   * POS-8856: pre-resolved line-item/type labels. Pure function has no ContentContext,
   * so the caller resolves these and passes them in.
   */
  labels: Pick<
    RetailInvoiceLabels,
    'proceeds' | 'totalProceed' | 'charge' | 'credit' | 'tax'
  > & {
    /** POS-8953: Resolved label for "Refund" (from ContentId.Refund). */
    refund: string;
    /** POS-8953: Resolved label for "Retail" prefix on payment references. */
    retailPrefix: string;
    /** POS-8953: Resolved label for "Credit Card" (Adyen payments where extPmtMethodType is null). */
    creditCard: string;
  };
  /** POS-8953: Resolver for the external payment method type label. */
  paymentMethodTypeLabel?: (type: ExternalPaymentMethodType) => string;
}

/**
 * POS-8630: Pure mapper from a SalesGroupDetails payload to RetailInvoicePDFProps,
 * producing the FULL multi-event group invoice (one order-detail row per member
 * sale). Reuses the already-multi-event RetailInvoicePDF component unchanged.
 *
 * Order-detail rows are built per member `Sale` (`group.sales`), but section / row /
 * seats / qty PREFER the flattened `ticketRows` entry (matched by saleId) when present,
 * because it carries the SPECIFIC sold seats for that sale (`seats` is a formatted single
 * seat or `first-last` span), falling back to the sale-level `seating` (section/row/
 * seatFr/seatTo, which reflect the whole listing/allocation span) and `qtySold` only when
 * no ticket row exists. Per-sale proceeds are likewise preferred from the ticket row (it
 * exposes a converted, group-currency `proceed`), otherwise falling back to the sale's own
 * `ttlNetProcs`.
 *
 * POS-8630: The member `Sale` type only carries event/venue IDs (viagVirtId), NOT
 * denormalized event name / event date / venue name. These are resolved via the
 * batch `catalog` arg (one CatalogClient.getCatalogSlim call for the whole group,
 * fetched by useGetGroupInvoiceCatalog): each sale's viagVirtId is matched against
 * `catalog.events` (event.virtId) for name/date, and the event's `venId` against
 * `catalog.venues` (venue.viagId) for the venue name.
 */
export function buildGroupInvoiceProps({
  group,
  brokerName,
  brokerEmail,
  catalog,
  labels,
  paymentMethodTypeLabel,
  paymentDetails,
}: BuildGroupInvoicePropsArgs): RetailInvoicePDFProps {
  const currency = group.currencyCode ?? '';

  // POS-8630: Build catalog lookup maps ONCE per invoice from the batch getCatalogSlim
  // result. events/venues are arrays (not keyed maps), so index them here: event by its
  // viagogo virtual id (event.virtId === sale.viagVirtId) and venue by its viagId
  // (event.venId === venue.viagId).
  const eventByVirtId = new Map(
    catalog?.events?.map((e) => [e.virtId, e]) ?? []
  );
  const venueByViagId = new Map(
    catalog?.venues?.map((v) => [v.viagId, v]) ?? []
  );

  // Index ticketRows by saleId so we can pull the converted, group-currency proceed
  // per member sale (one ticketRow per sale). Cancelled rows are included by the API
  // but excluded from money aggregates; we still show them as order-detail rows.
  const ticketRowBySaleId = new Map(
    group.ticketRows.map((row) => [row.saleId, row])
  );

  // orderDetailRows: ONE row per member sale.
  const orderDetailRows: RetailInvoicePDFProps['orderDetailRows'] =
    group.sales.map((sale) => {
      const ticketRow = ticketRowBySaleId.get(sale.id);
      const proceed = ticketRow?.proceed?.amt ?? sale.ttlNetProcs?.amt ?? 0;
      // Resolve this member sale's event (by viagVirtId) and its venue (by venId)
      // from the batch catalog.
      const event = eventByVirtId.get(sale.viagVirtId);
      const venue =
        event?.venId != null ? venueByViagId.get(event.venId) : undefined;
      return {
        // POS-8630: when a sale's viagVirtId isn't present in the batch catalog
        // (event.virtId), event/venue fall back to undefined and the PDF renders "—".
        // section/row/seats/qty prefer the flattened ticketRow (specific sold seats)
        // and fall back to the sale-level seating (listing/allocation span).
        eventName: event?.name ?? undefined,
        eventDate: event?.dates?.start ?? undefined,
        venueName: venue?.name ?? undefined,
        section: ticketRow?.section ?? sale.seating?.section ?? undefined,
        row: ticketRow?.row ?? sale.seating?.row ?? undefined,
        seatFrom: sale.seating?.seatFr ?? undefined,
        seatTo: sale.seating?.seatTo ?? undefined,
        seats: ticketRow?.seats ?? undefined,
        qty: ticketRow?.qty ?? sale.qtySold,
        proceed,
      };
    });

  // Financial breakdown line items.
  const subtotalAmount = group.subtotal?.amt ?? 0;
  const totalProceedsAmount = group.totalProceeds?.amt ?? subtotalAmount;

  const lineItems: RetailInvoicePDFProps['lineItems'] = [
    {
      description: labels.totalProceed,
      amount: totalProceedsAmount,
    },
    // earningLines → charge/credit/tax rows. A charge or tax ADDS to the amount
    // owed (positive); a credit REDUCES it (negative). owed = proceeds + charges
    // + tax − credits.
    // POS-8856: the upstream sign of earning-line amounts is INCONSISTENT across paths
    // (some treat charges/credits as positive magnitudes, others deliver credits
    // negative). Normalize to a magnitude via Math.abs and re-apply the sign from the
    // authoritative isCredit flag, so a charge/tax is +magnitude and a credit is
    // −magnitude regardless of the incoming sign. No-op for already-positive data.
    // POS-8630: SalesGroupEarningLine has NO rate % field, so ratePercent is left
    // undefined and the Rate % column renders blank ("—") for group charges/credits.
    ...group.earningLines.map((line) => {
      const typeLabel =
        line.costType === SaleCostType.Taxes
          ? labels.tax
          : line.isCredit
            ? labels.credit
            : labels.charge;
      return {
        description: line.description ?? typeLabel,
        costType: typeLabel,
        amount: (line.isCredit ? -1 : 1) * Math.abs(line.amount?.amt ?? 0),
      };
    }),
  ];

  // POS-8953: Payment lines mirror the modal's PaymentSection layout:
  //   1. Original payment transactions (any state, positive amounts) — shows
  //      what was originally paid, persisting through refunds.
  //   2. One aggregated refund line (negative amount, red styling) — when any
  //      Reversed/PendingReversal lines exist.
  // Exclude credit-refund split lines (partial refund artifacts) from the
  // payment rows — they are not original transactions and would double-count
  // with the aggregated refund line.
  const allPaymentLines = group.aggregatedPaymentLines ?? [];

  // Original payment transaction rows (persist regardless of state)
  const paymentLineItems: RetailInvoicePDFProps['lineItems'] = allPaymentLines
    .filter(
      (line) =>
        (line.state === MarketplacePaymentState.Paid ||
          line.state === MarketplacePaymentState.Reversed ||
          line.state === MarketplacePaymentState.PendingReversal) &&
        !line.extLineId?.startsWith('credit-refund-')
    )
    .map((line) => {
      const reference = line.extPmtId || `${line.id}`;
      return {
        description: `${labels.retailPrefix} - ${reference}`,
        amount: line.amt?.amt ?? 0,
        paymentReferenceId: reference,
        referenceLabel: `${labels.retailPrefix} - ${reference}`,
        referenceUrl: line.posPmtId
          ? getMarketplacePaymentRelativeUrl(line.posPmtId)
          : undefined,
        paymentMethodLabel:
          line.extPmtMethodType != null && paymentMethodTypeLabel
            ? line.extCustomPmtTypeName &&
              line.extPmtMethodType === ExternalPaymentMethodType.Other
              ? line.extCustomPmtTypeName
              : paymentMethodTypeLabel(
                  line.extPmtMethodType as ExternalPaymentMethodType
                )
            : labels.creditCard,
      };
    });

  // One aggregated refund row (red) — matches RefundPaymentRow in the modal
  const refundTotal = allPaymentLines
    .filter(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    )
    .reduce((sum, l) => sum + Math.abs(l.amt?.amt ?? 0), 0);

  if (refundTotal > 0) {
    // Refund to credit always credits the buyer's account balance.
    paymentLineItems.push({
      description: labels.refund,
      amount: -refundTotal,
      referenceLabel: labels.refund,
      paymentMethodLabel: paymentMethodTypeLabel
        ? paymentMethodTypeLabel(ExternalPaymentMethodType.CustomerCredit)
        : labels.creditCard,
    });
  }

  // POS-8726: Sum payments including refunds (reversed lines are negative)
  const totalPaid = (group.aggregatedPaymentLines ?? [])
    .filter((l) => l.state === MarketplacePaymentState.Paid)
    .reduce((sum, l) => sum + (l.amt?.amt ?? 0), 0);
  const totalRefunded = (group.aggregatedPaymentLines ?? [])
    .filter(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    )
    .reduce((sum, l) => sum + (l.amt?.amt ?? 0), 0);
  const netPaymentsMade = totalPaid - totalRefunded;
  const paymentsMade =
    netPaymentsMade > 0
      ? netPaymentsMade
      : (group.totalPaymentReceived?.amt ?? undefined);

  // Representative member sale for invoice number/date fallbacks.
  const representativeSale = group.sales[0];

  // POS-8630: The invoice header (Total Invoice Value) and the Subtotal the component
  // computes from the owed line items must match. owed = proceeds + charges + tax
  // − credits. The earning lines carry charges, credits AND tax (backend maps
  // MarketplacePaymentLineType.Tax → SaleCostType.Taxes), so tax is folded in here.
  const owedAmount =
    totalProceedsAmount +
    group.earningLines.reduce(
      // POS-8856: magnitude-normalize then re-sign by isCredit (see lineItems above),
      // so this matches the Subtotal the component computes regardless of upstream sign.
      (sum, line) =>
        sum + (line.isCredit ? -1 : 1) * Math.abs(line.amount?.amt ?? 0),
      0
    );

  const hasReversedLines = (group.aggregatedPaymentLines ?? []).some(
    (l) =>
      l.state === MarketplacePaymentState.Reversed ||
      l.state === MarketplacePaymentState.PendingReversal
  );
  // Balance due = owed − paid − refunded, computed from components so it (a) includes
  // tax, which the backend `balanceDue` excludes, and (b) avoids the backend's clamp
  // at 0 which would otherwise hide an overpaid-by-tax balance. Refunded amounts count
  // as "settled" (POS-8726) — the seller chose to refund, so they are not owed. When
  // nothing is paid, this equals the full owed amount (POS-8918).
  const balanceDue = Math.max(0, owedAmount - totalPaid - totalRefunded);

  // POS-8726: Determine invoice status — "Refunded" for fully refunded sales
  const isFullyRefunded = hasReversedLines && totalPaid === 0;
  const status: RetailInvoicePDFProps['status'] = isFullyRefunded
    ? 'Refunded'
    : balanceDue > 0
      ? 'Outstanding'
      : 'Paid';

  return {
    brokerName,
    brokerEmail,
    buyerName: group.buyer?.name ?? undefined,
    buyerEmail: group.buyer?.email ?? undefined,
    invoiceNumber: group.salesGroupName || group.salesGroupId,
    invoiceDate: representativeSale?.saleDate ?? group.insertDate,
    currency,
    // POS-8630: header Total Invoice Value = amount OWED (proceeds + charges − credits),
    // which equals the Subtotal the component computes from the owed line items.
    totalAmount: owedAmount,
    lineItems: [...lineItems, ...paymentLineItems],
    status,
    balanceDue,
    paymentsMade,
    orderDetailRows,
  };
}
